import axios from 'axios';
import JSONBig from 'json-bigint';
import { Storage } from './storage';
import { ElMessage } from 'element-plus'; 
import { handleAuthExpired } from './auth'

export type ApiResponse<T = any> = {
	code: number;
	msg: string;
	data: T;
	ok?: boolean;
};

// 扩展 AxiosRequestConfig 以支持自定义参数
declare module 'axios' {
	export interface AxiosRequestConfig {
		silentError?: boolean;
		autoMessage?: boolean;
	}
}

const envBaseURL = (import.meta as any).env?.VITE_API_BASE_URL || '';
const baseURL = (import.meta as any).env?.DEV
	? (envBaseURL || '/api')
	: (envBaseURL === '/api' ? '' : envBaseURL);

export const http = axios.create({
	baseURL,
	timeout: 30000,
	// 使用 json-bigint 处理超大数字（例如雪花算法的长整型 ID），将其解析为字符串以防止精度丢失
	transformResponse: [
		function (data) {
			try {
				return JSONBig({ storeAsString: true }).parse(data);
			} catch (err) {
				return data;
			}
		},
	],
});

http.interceptors.request.use((config) => {
	const token = Storage.getToken();
	const tenantId = Storage.getTenantId();
	const schoolId = Storage.getSchoolId();
	
	// 在 axios 1.x 中 headers 不能直接赋值为 {}，需要使用 set 方法或直接修改属性
	if (!config.headers) {
		config.headers = {} as any;
	}

	// 说明：获取 token 的接口自身不能再带旧 token，否则容易触发 401/循环刷新
	if (token && !config.headers['X-Skip-Token']) {
		config.headers.Authorization = `Bearer ${token}`;
	}
	// 说明：极少数接口（如公共配置）不需要租户头；这里提供可选开关
	if (tenantId && !config.headers['X-Skip-Tenant']) {
		config.headers['TENANT-ID'] = tenantId;
	}
	if (schoolId && !config.headers['X-Skip-School']) {
		config.headers['X-School-Id'] = schoolId;
	}

	return config;
});

http.interceptors.response.use(
	(resp) => {
		const resData = resp.data as ApiResponse;
		const config = resp.config;

		if (resData && typeof resData.code === 'number' && resData.code !== 0) {
			const errorMsg = resData.msg || '请求失败';
			const isSilent = config.silentError === true;
			const autoMessage = config.autoMessage === true;
			if (autoMessage && !isSilent && errorMsg) {
				ElMessage.error(errorMsg);
			}
			return resp;
		}

		return resp;
	},
	(err) => {
		const status = err?.response?.status;
		const config = err?.config || {};
		
		// 判断是否是 resolve 接口本身，避免 resolve 失败后重定向到 platform 再次触发 resolve，导致死循环
		const isResolveApi = config.url && config.url.includes('/tenant/resolve');
		const isLoginApi = config.url && config.url.includes('/oauth2/token');
		const host = typeof window !== 'undefined' ? window.location.host : '';

		if ((status === 424 || status === 401) && !isResolveApi && !isLoginApi) {
			handleAuthExpired();
			return Promise.reject(err?.response?.data || err);
		}

		if (status === 426 && !isResolveApi) {
			const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
			if (typeof window !== 'undefined') {
				const doFallback = async () => {
					try {
						const res = await axios.request({
							baseURL,
							url: '/admin/tenant/resolve',
							method: 'get',
							params: host ? { host } : undefined,
							headers: { 'X-Skip-Token': '1', 'X-Skip-Tenant': '1' },
							timeout: 8000,
						});
						const tenantId = String((res as any)?.data?.data?.tenantId || '');
						if (tenantId) {
							Storage.setTenantId(tenantId);
							if (currentPath.startsWith('/platform')) {
								window.location.href = '/school';
							}
							return;
						}
					} catch {
					}
					Storage.clearTenantId();
					Storage.clearToken();
				};
				doFallback();
			}
		}
		const isSilent = config.silentError === true;
		const autoMessage = config.autoMessage === true;
		const errorMsg = err?.response?.data?.msg || '请求发生错误';
		if (autoMessage && !isSilent) {
			ElMessage.error(errorMsg);
		}
		return Promise.reject(err?.response?.data || err);
	}
);

export async function request<T = any>(cfg: Parameters<typeof http.request>[0]): Promise<ApiResponse<T>> {
	const res = await http.request(cfg);
	return res.data as ApiResponse<T>;
}
